/**
 * useDocSaveFailureBuffer — save-failure surfacing + unsaved-content buffer
 * for the Drive doc editor (#2671).
 *
 * When PATCH /notes/:id fails (e.g. the persistent prod 500 in #2671), the
 * editor used to swallow the error with a bare console.error — every edit was
 * silently dropped and a close discarded the buffer entirely. This hook owns:
 *
 * - `saveError`: a visible error string, deliberately DISTINCT from the
 *   modal's `saveStatus` (a 10s interval re-derives `saveStatus` from
 *   `lastSavedAt`, so an error written there would be clobbered). The render
 *   site gives `saveError` precedence; it clears on the next successful save.
 * - An error toast on autosave failure (`reportSaveFailure`); auto-checkpoint
 *   failures pass `{ toast: false }` for status-only treatment.
 * - A localStorage stash (`xavior:doc-unsaved:<docId>`) written when the
 *   final save-on-close fails (`stashUnsavedOnClose`). A successful save
 *   (`handleSaveSuccess`) clears it ONLY once the mount GET has adjudicated
 *   it (`markStashSafeToClear`): if the GET failed (stash never adjudicated)
 *   or the conflict-recovery checkpoint PATCH failed (stash deliberately
 *   kept for the next open), the stash is the only copy of the buffered
 *   content and must survive later successful saves. No stash is written
 *   when the final save succeeds.
 *
 * The stash records the `etag` of the last successfully saved version. The
 * RESTORE decision deliberately does NOT live here: DocEditorModal's mount
 * GET adjudicates the stash against the live server copy — restore on etag
 * match, preserve to version history on mismatch — so a doc edited elsewhere
 * between the failed close and the reopen (chat-agent edits, another
 * device/tab) is never silently overwritten. Pre-etag stashes parse with
 * `etag: null` and are treated as conflicts (the safer path).
 */
import { useCallback, useRef, useState } from 'react';
import { useToast } from '@/app/providers/ToastContext';

export const DOC_SAVE_ERROR_MESSAGE = 'Save failed — changes not saved';

export interface UnsavedDocStash {
  title: string;
  body: string;
  savedAt: string;
  /** ETag of the last successfully saved version at stash time; null for pre-etag stashes (treated as a conflict on restore). */
  etag: string | null;
}

/** All storage keys are `xavior:`-prefixed (apps/web/AGENTS.md). */
export function unsavedDocStashKey(docId: string): string {
  return `xavior:doc-unsaved:${docId}`;
}

export function readUnsavedDocStash(docId: string): UnsavedDocStash | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(unsavedDocStashKey(docId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<UnsavedDocStash> | null;
    if (!parsed || typeof parsed.body !== 'string') return null;
    return {
      title: typeof parsed.title === 'string' ? parsed.title : '',
      body: parsed.body,
      savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : '',
      etag: typeof parsed.etag === 'string' ? parsed.etag : null,
    };
  } catch {
    // Corrupt JSON or storage unavailable — treat as no stash.
    return null;
  }
}

export function clearUnsavedDocStash(docId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(unsavedDocStashKey(docId));
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

export function useDocSaveFailureBuffer(docId: string): {
  saveError: string | null;
  reportSaveFailure: (opts?: { toast?: boolean }) => void;
  handleSaveSuccess: () => void;
  stashUnsavedOnClose: (title: string, body: string, etag: string | null) => void;
  markStashSafeToClear: () => void;
} {
  const { showToast } = useToast();
  const [saveError, setSaveError] = useState<string | null>(null);

  // Toast only on the no-error → error TRANSITION: under a persistent 500
  // with active typing (the exact #2671 scenario) autosave fails on every
  // 1.5s pause and ToastContext stacks each error toast for 8s — without
  // dedupe identical toasts pile up. Re-armed by the next successful save.
  // (Same rapid-fire dedupe stance as ToastContext's patch-failed listener.)
  const saveErrorActiveRef = useRef(false);

  /** An autosave/auto-checkpoint failed: show the error status (toast optional). */
  const reportSaveFailure = useCallback((opts?: { toast?: boolean }) => {
    setSaveError(DOC_SAVE_ERROR_MESSAGE);
    if (opts?.toast !== false && !saveErrorActiveRef.current) {
      showToast(DOC_SAVE_ERROR_MESSAGE, 'error');
    }
    saveErrorActiveRef.current = true;
  }, [showToast]);

  // Successful saves may only drop the stash once the mount GET has actually
  // adjudicated it (no stash existed / equality-drop / restore applied /
  // conflict checkpoint persisted). Until then — the GET failed, or the
  // recovery-checkpoint PATCH failed and the stash is deliberately kept for
  // the next open — clearing here would silently destroy the only copy of
  // the buffered content (#3927 review).
  const stashSafeToClearRef = useRef(false);

  /** The mount GET adjudicated the stash (or found none): successful saves may now clear it. */
  const markStashSafeToClear = useCallback(() => {
    stashSafeToClearRef.current = true;
  }, []);

  /** Any successful save: clear the visible error; drop the stash only once adjudicated. */
  const handleSaveSuccess = useCallback(() => {
    setSaveError(null);
    saveErrorActiveRef.current = false;
    if (stashSafeToClearRef.current) clearUnsavedDocStash(docId);
  }, [docId]);

  /**
   * The final save-on-close failed: buffer the content instead of discarding
   * it. `etag` is the last successfully saved version — the restore-time
   * conflict check compares it against the live server etag.
   */
  const stashUnsavedOnClose = useCallback((title: string, body: string, etag: string | null) => {
    if (typeof window === 'undefined') return;
    try {
      const stash: UnsavedDocStash = { title, body, savedAt: new Date().toISOString(), etag };
      window.localStorage.setItem(unsavedDocStashKey(docId), JSON.stringify(stash));
      showToast('Save failed — your changes are kept on this device and will be restored when you reopen this doc', 'error');
    } catch {
      // localStorage full/unavailable — the failure is already logged; at least tell the user.
      showToast(DOC_SAVE_ERROR_MESSAGE, 'error');
    }
  }, [docId, showToast]);

  return { saveError, reportSaveFailure, handleSaveSuccess, stashUnsavedOnClose, markStashSafeToClear };
}
